import Dexie from 'dexie';

/**
 * Database schema version history
 * Each version upgrade should be additive to maintain backwards compatibility
 */

export const CURRENT_VERSION = 2;

/**
 * Apply schema to a Dexie database instance
 * @param {Dexie} db - Dexie database instance
 */
export function applySchema(db) {
  // V1: initial schema
  db.version(1).stores({
    cases: '_id, creator, updatedAt, revision',
    jurors: '_id, caseId, seatNumber, alternateSeatNumber, revision, [caseId+seatNumber], [caseId+alternateSeatNumber]',
    pendingOperations: '++id, entityType, entityId, operationType, caseId, createdAt',
    syncMetadata: 'key'
  });

  // V2: add compound index [entityType+entityId] for efficient lookups
  db.version(2).stores({
    cases: '_id, creator, updatedAt, revision',
    jurors: '_id, caseId, seatNumber, alternateSeatNumber, revision, [caseId+seatNumber], [caseId+alternateSeatNumber]',
    pendingOperations: '++id, [entityType+entityId], entityType, entityId, operationType, caseId, createdAt',
    syncMetadata: 'key'
  });

  return db;
}

/**
 * Case record structure
 * @typedef {Object} CaseRecord
 * @property {string} _id - Server-assigned ID or local UUID
 * @property {string} name - Case name
 * @property {string} [number] - Case number
 * @property {string} [details] - Case details
 * @property {string} creator - Creator's email
 * @property {number} juryBoxSize - Number of seats in jury box
 * @property {number} jurorsPerRow - Jurors per row in display
 * @property {number} numberOfAlternates - Number of alternate jurors
 * @property {number[]} spacers - Spacer positions
 * @property {string[]} demographicCategories - Demographic categories (legacy, plain name list — always kept in sync server-side)
 * @property {DemographicCategoryConfig[]} [demographicCategoryConfigs] - Additive: per-category options/strictness config
 * @property {ScoreDefinition[]} [scoreDefinitions] - Additive: named scores (e.g. "Eligibility")
 * @property {ScoringRule[]} [scoringRules] - Additive: category+value -> score delta rules
 * @property {string} [templateAppliedFrom] - Template._id last explicitly applied to this case, or absent/null
 * @property {number} [templateAppliedRevision] - Template.revision at the time it was last applied
 * @property {number} prosecutionPeremptStrikes - Available prosecution strikes
 * @property {number} defendantPeremptStrikes - Available defense strikes
 * @property {number} revision - Server revision number
 * @property {string} createdAt - ISO timestamp
 * @property {string} updatedAt - ISO timestamp
 * @property {boolean} [_localOnly] - True if not yet synced to server
 * @property {string} [_localId] - Original local ID if created offline
 */

/**
 * Per-category option/strictness configuration, additive alongside the
 * legacy `demographicCategories` bare name list. Never required for older
 * clients to keep working — see the "Demographics: Scoring + Predefined
 * Options" plan's hard backward-compatibility constraint.
 * @typedef {Object} DemographicCategoryConfig
 * @property {string} _id
 * @property {string} name
 * @property {string[]} options - Predefined values offered in the picker
 * @property {boolean} allowFreeform - Whether values outside `options` may be entered
 * @property {string|null} [sourceTemplateId] - Template this row was cloned from, or null for case-only rows
 */

/**
 * A named, user-defined score (e.g. "Eligibility", "General Fit"). A juror's
 * score is the sum of matching ScoringRule deltas — always computed
 * client-side, never stored server-side.
 * @typedef {Object} ScoreDefinition
 * @property {string} _id
 * @property {string} name
 * @property {string} [description]
 * @property {boolean} [showOnJurorCard] - Defaults to true when absent
 * @property {string} [color] - Explicit hex override for badge color; falls back to a
 *   position-based palette color (see util/scoreColors) when absent
 * @property {string|null} [sourceTemplateId]
 */

/**
 * Maps a demographic category+value to a delta on a named score.
 * `optionValue: null` is special: it's the category's "no answer" rule,
 * applied when a juror has no `demographics` entry at all for `categoryName`
 * (distinct from a rule targeting a specific, possibly empty-string, value).
 * @typedef {Object} ScoringRule
 * @property {string} _id
 * @property {string} categoryName
 * @property {string|null} optionValue - null = "no answer" rule for this category
 * @property {string} scoreDefinitionId
 * @property {number} delta
 * @property {string|null} [sourceTemplateId]
 */

/**
 * Standalone, creator-owned, ACL-shareable resource. Not stored in Dexie —
 * template CRUD/sharing are plain online-only calls (see db/sync/templateApi.js).
 * @typedef {Object} TemplateRecord
 * @property {string} _id
 * @property {string} name
 * @property {string} creator - Creator's email
 * @property {Array<{_id: string, name: string, options: string[], allowFreeform: boolean}>} categoryTemplates
 * @property {ScoreDefinition[]} scoreDefinitions
 * @property {Array<{_id: string, categoryName: string, optionValue: string|null, scoreDefinitionId: string, delta: number}>} scoringRules
 * @property {number} revision
 */

/**
 * Juror record structure
 * @typedef {Object} JurorRecord
 * @property {string} _id - Server-assigned ID or local UUID
 * @property {string} caseId - Parent case ID
 * @property {string} [nameAndId] - Name and ID combined
 * @property {number} [seatNumber] - Seat in jury box (null if in pool)
 * @property {number} [alternateSeatNumber] - Alternate seat number
 * @property {string} [comments] - Comments/notes
 * @property {Object} [commentsStyles] - Rich text styles for comments
 * @property {Array} demographics - Array of {name, value} demographic entries
 * @property {boolean} [discarded] - Whether juror is dismissed
 * @property {boolean} [alternateDiscarded] - Whether alternate is dismissed
 * @property {string} [discardedReason] - 'peremptory' | 'cause' | 'hardship'
 * @property {string} [discarder] - 'prosecutor' | 'defense' | 'both'
 * @property {string} [color] - Background color
 * @property {number} revision - Server revision number
 * @property {string} created - ISO timestamp
 * @property {string} updated - ISO timestamp
 * @property {boolean} [_localOnly] - True if not yet synced to server
 * @property {string} [_localId] - Original local ID if created offline
 */

/**
 * Pending operation structure
 * @typedef {Object} PendingOperation
 * @property {number} id - Auto-incremented ID
 * @property {string} entityType - 'case' | 'juror'
 * @property {string} entityId - ID of the entity
 * @property {string} [caseId] - Parent case ID (for jurors)
 * @property {string} operationType - 'create' | 'update' | 'delete'
 * @property {Object} [diff] - Changed fields (for updates)
 * @property {Object} [data] - Full data (for creates)
 * @property {number} baseRevision - Revision this change is based on
 * @property {string} createdAt - ISO timestamp
 */

/**
 * Sync metadata structure
 * @typedef {Object} SyncMetadata
 * @property {string} key - Metadata key
 * @property {*} value - Metadata value
 */

export default { applySchema, CURRENT_VERSION };
